/**
 * Flow Optimizer — "Move to live now": plan, then promote.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Two steps on purpose. The plan is a READ (so a disabled button can explain
 * itself, and a swap can name its victim before anything is touched); the
 * promote is the write. The server re-derives everything on the write, so a
 * dialog left open while the rotation moved on cannot act on stale truth.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { FlowLayer } from '@trafficpulse/shared';

import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';
import {
  planPromote,
  promoteToLive,
} from '../services/flow-optimizer-queue-api';
import { queueKeys } from './useFlowOptimizerQueue';

export interface PromoteArgs {
  layer: FlowLayer;
  bankId: string;
  /** The live element to park in Waiting; only when the pool is at capacity. */
  victimBankId?: string;
}

/** Read-only: what would happen if this element went live right now. */
export function usePromotePlan(
  campaignId: string,
  provider: QueueProvider = 'voluum',
) {
  return useMutation({
    mutationFn: (args: PromoteArgs) => planPromote(campaignId, args, provider),
  });
}

/** The write. Refreshes the queue, the rotation history and every timeline. */
export function usePromoteToLive(
  campaignId: string,
  provider: QueueProvider = 'voluum',
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: PromoteArgs) => promoteToLive(campaignId, args, provider),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queueKeys.queue(provider, campaignId) });
      qc.invalidateQueries({
        queryKey: queueKeys.rotationHistory(provider, campaignId),
      });
      // Both the promoted element and the parked victim gained an event.
      qc.invalidateQueries({
        queryKey: [provider, 'flow-optimizer', 'queue-events', campaignId],
      });
    },
  });
}
